import React, { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import addDays from 'date-fns/addDays';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import isValid from 'date-fns/isValid';
import { useStyles } from './AllFlagsPage.styles';
import { getAllFlagsData, setFlagAuthorsIds, setFlagManagersIds, setInvolvedUsersIds } from './redux/actions';
import { AllFlagsFilters } from './components/AllFlagsFilters/AllFlagsFilters';
import { FilterFields } from './enums/FilterFields';
import { useAllFlagsData } from './hooks/useAllFlagsData';
import { FlagsTable } from '../../components/FlagsTable';
import {
  ALL_FLAGS_FETCH_LIMIT,
  DEFAULT_PAGINATION_DATA,
  projectEndObject,
  statusObject,
  userStatusesObject,
} from './constants/constants';
import { FlagsTableHeader } from '@modules/HappinessModule/enums/FlagsTableHeader';
import { FlagsSortField } from '@modules/HappinessModule/enums/FlagsSortField';
import useUserEnabledFeatures from '@shared/hooks/useUserEnabledFeatures';
import { AccessDeniedView } from '@shared/components/DataErrorView/components/AccessDeniedView';
import { DataState } from '@shared/enums/DataState';
import { useToms } from '@shared/hooks/useToms';
import {
  setAllLocationsDataLoading,
  setFlagAuthorsData,
  setFlagAuthorsLoading,
  setFlagManagersData,
  setFlagManagersLoading,
  setInvolvedUsersData,
  setInvolvedUsersLoading,
  setSortInfo,
} from './redux';
import { usePrs } from '@shared/hooks/usePrs';
import { useAllDepartments } from '@shared/hooks/useAllDepartments';
import { AllFlagsDataFilters } from '@modules/HappinessModule/interfaces/AllFlagsDataFilters';
import { labelsFromValuesArr } from './helpers/labelsFromValues';
import { idToUser } from '@shared/helpers/idToUser';
import { projectIdsToName } from '@shared/helpers/projectIdsToName';
import {
  selecFlagManagersData,
  selectFlagAuthorsData,
  selectFlagAuthorsState,
  selectFlagManagersState,
  selectInvolvedUsersData,
  selectInvolvedUsersState,
  getAllFlagsSortInfoSelector,
} from './redux/selectors';
import { commaToArrayParser } from '@shared/helpers/commaToArrayParser';
import { UserStatus } from '@shared/enums/UserStatus';
import { Typography } from '@mui/material';
import { FlagFiltersFields } from './interfaces/FlagFiltersFields';
import { DataErrorView } from '@shared/components/DataErrorView';
import { SortInfo } from '@shared/interfaces/sortInfo';

export const AllFlagsPage: React.FC = () => {
  const styles = useStyles();
  const { allFlags } = useAllFlagsData();
  const allFlagsData = allFlags.data?.items ?? [];
  const totalCount = allFlags.data?.totalCount ?? 0;
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { isFlagsEnabled } = useUserEnabledFeatures();
  const { data: tomData, state } = useToms();
  const prs = usePrs();
  const allDepartments = useAllDepartments({ onlyActive: false });
  const isDataLoading =
    allFlags.state === DataState.Pending ||
    state === DataState.Pending ||
    prs.state === DataState.Pending ||
    allDepartments.state === DataState.Pending;

  const isRejected = allFlags.state === DataState.Rejected;

  const flagInvolvedUserState = useSelector(selectInvolvedUsersState);
  const flagAuthorsState = useSelector(selectFlagAuthorsState);
  const flagManagersState = useSelector(selectFlagManagersState);
  const isFilterDataLoading =
    state === DataState.Pending ||
    prs.state === DataState.Pending ||
    allDepartments.state === DataState.Pending ||
    flagInvolvedUserState === DataState.Pending ||
    flagAuthorsState === DataState.Pending ||
    flagManagersState === DataState.Pending;
  const statusesFromParams = searchParams.get(FilterFields.Status);
  const userStatusesFromParam = searchParams.get(FilterFields.UserStatuses);
  const assignedTomsFromParams = searchParams.get(FilterFields.AssignedTomIds);
  const assignedPrsFromParams = searchParams.get(FilterFields.AssignedPrIds);
  const legalLocationsFromParams = searchParams.get(FilterFields.LegalLocations);
  const assignedProjectsFromParams = searchParams.get(FilterFields.AssignedProjects);
  const affectedProjectsFromParams = searchParams.get(FilterFields.AffectedProjects);
  const involvedUserFromParams = searchParams.get(FilterFields.InvolvedUsers);
  const flagAuthorsFromParams = searchParams.get(FilterFields.FlagAuthors);
  const reasonsSubReasonsFromParams = searchParams.get(FilterFields.ReasonsSubReasons);
  const flagManagersFromParams = searchParams.get(FilterFields.FlagManagers);
  const involvedUserIds = involvedUserFromParams?.split(',');
  const flagAuthorsIds = flagAuthorsFromParams?.split(',');
  const flagManagersIds = flagManagersFromParams?.split(',');
  const sortInfo = useSelector(getAllFlagsSortInfoSelector);
  const involvedUsersData = useSelector(selectInvolvedUsersData) ?? [];
  const flagAuthorsData = useSelector(selectFlagAuthorsData) ?? [];
  const flagManagersData = useSelector(selecFlagManagersData) ?? [];

  const initialValues: FlagFiltersFields = {
    severity: searchParams.get(FilterFields.Severity) ?? 'all',
    status: statusesFromParams ? labelsFromValuesArr(statusesFromParams, statusObject) : [statusObject.all.label],
    isNewHomeNeeded: Boolean(searchParams.get(FilterFields.IsNewHomeNeeded)) ?? false,
    isAnotherOfferInvolved: Boolean(searchParams.get(FilterFields.isAnotherOfferInvolved)) ?? false,
    projectEnd: searchParams.get(FilterFields.ProjectEnd) ?? projectEndObject.anyTime.value,
    userStatuses: userStatusesFromParam
      ? labelsFromValuesArr(userStatusesFromParam, userStatusesObject)
      : [userStatusesObject.all.label],
    involvedUsers: idToUser(involvedUsersData, involvedUserFromParams),
    flagAuthors: idToUser(flagAuthorsData, flagAuthorsFromParams),
    flagManagers: idToUser(flagManagersData, flagManagersFromParams),
    assignedTomIds: assignedTomsFromParams ? idToUser(tomData ?? [], assignedTomsFromParams) : [],
    assignedPrIds: assignedPrsFromParams ? idToUser(prs.data ?? [], assignedPrsFromParams) : [],
    legalLocations: legalLocationsFromParams ? legalLocationsFromParams.split(',') : [],
    assignedProjects: assignedProjectsFromParams
      ? projectIdsToName(assignedProjectsFromParams.split(','), allDepartments.data ?? [])
      : [],
    affectedProjects: affectedProjectsFromParams
      ? projectIdsToName(affectedProjectsFromParams.split(','), allDepartments.data ?? [])
      : [],
    reasonsSubReasons: reasonsSubReasonsFromParams?.split(',') ?? [],
    resolvedDateFrom: searchParams.get(FilterFields.ResolvedDateFrom),
    resolvedDateTo: searchParams.get(FilterFields.ResolvedDateTo),
    escalationDateFrom: searchParams.get(FilterFields.EscalationDateFrom),
    escalationDateTo: searchParams.get(FilterFields.EscalationDateTo),
    isEscalatedByClient: Boolean(searchParams.get(FilterFields.IsEscalatedByClient)) ?? false,
  };

  useEffect(() => {
    if (involvedUserIds) dispatch(setInvolvedUsersIds(involvedUserIds));
    else dispatch(setInvolvedUsersData({ undefined, state: DataState.Fulfilled }));

    if (flagAuthorsIds) dispatch(setFlagAuthorsIds(flagAuthorsIds));
    else dispatch(setFlagAuthorsData({ undefined, state: DataState.Fulfilled }));

    if (flagManagersIds) dispatch(setFlagManagersIds(flagManagersIds));
    else dispatch(setFlagManagersData({ undefined, state: DataState.Fulfilled }));

    return () => {
      dispatch(setAllLocationsDataLoading());
      dispatch(setFlagManagersLoading());
      dispatch(setFlagAuthorsLoading());
      dispatch(setInvolvedUsersLoading());
    };
  }, []);

  const getFlagPayload = (overrides?: Partial<AllFlagsDataFilters>): AllFlagsDataFilters => {
    const limit = overrides?.limit ?? ALL_FLAGS_FETCH_LIMIT;
    const offset = overrides?.offset ?? DEFAULT_PAGINATION_DATA.offset;
    const statuses = commaToArrayParser(searchParams.get(FilterFields.Status));
    const authorIds = commaToArrayParser(searchParams.get(FilterFields.FlagAuthors));
    const primaryReportsToIds = commaToArrayParser(searchParams.get(FilterFields.FlagManagers));
    const assignedTomIds = commaToArrayParser(searchParams.get(FilterFields.AssignedTomIds));
    const assignedPrIds = commaToArrayParser(searchParams.get(FilterFields.AssignedPrIds));
    const userIds = commaToArrayParser(searchParams.get(FilterFields.InvolvedUsers));
    const countries = commaToArrayParser(searchParams.get(FilterFields.LegalLocations));
    const projectIds = commaToArrayParser(searchParams.get(FilterFields.AssignedProjects));
    const affectedProjectIds = commaToArrayParser(searchParams.get(FilterFields.AffectedProjects));
    const severity = searchParams.get(FilterFields.Severity) ?? undefined;
    const userStatuses = commaToArrayParser(searchParams.get(FilterFields.UserStatuses));

    const payload: AllFlagsDataFilters = {
      isNewHomeNeeded: searchParams.get(FilterFields.IsNewHomeNeeded) === 'true' ? true : undefined,
      isAnotherOfferInvolved: searchParams.get(FilterFields.isAnotherOfferInvolved) === 'true' ? true : undefined,
      isEscalatedByClient: searchParams.get(FilterFields.IsEscalatedByClient) === 'true' ? true : undefined,
      assignedTomIds,
      assignedPrIds,
      countries,
      projectIds,
      affectedProjectIds,
      limit,
      offset,
      statuses,
      userStatuses,
      severity,
      userIds,
      primaryReportsToIds,
      authorIds,
      reasonsSubReasons: commaToArrayParser(searchParams.get(FilterFields.ReasonsSubReasons)) ?? [],
      sortInfo,
    };

    const today = new Date();
    const beginningOfTime = new Date(0);
    const todayPlusTwoWeeks = addDays(today, 14);
    const projectEnd = searchParams.get(FilterFields.ProjectEnd);
    switch (projectEnd) {
      case projectEndObject.anyTime.value:
        break;
      case projectEndObject.next2Weeks.value:
        payload.lastDayOnTheProjectTo = endOfDay(todayPlusTwoWeeks).toISOString();
        payload.lastDayOnTheProjectFrom = startOfDay(today).toISOString();
        break;
      case projectEndObject.alreadyEnded.value:
        payload.lastDayOnTheProjectTo = endOfDay(today).toISOString();
        payload.lastDayOnTheProjectFrom = startOfDay(beginningOfTime).toISOString();
        break;
    }
    if (userStatuses?.includes(userStatusesObject.inactive.value)) {
      payload.userStatuses = payload.userStatuses?.filter((status) => status !== userStatusesObject.inactive.value);
      payload.userStatuses?.push(UserStatus.Disabled);
    }

    const resolvedDateFrom = searchParams.get(FilterFields.ResolvedDateFrom);
    const resolvedDateTo = searchParams.get(FilterFields.ResolvedDateTo);
    const escalationDateFrom = searchParams.get(FilterFields.EscalationDateFrom);
    const escalationDateTo = searchParams.get(FilterFields.EscalationDateTo);

    if (resolvedDateFrom) {
      const date = new Date(resolvedDateFrom);
      if (isValid(date)) {
        payload.resolvedDateFrom = startOfDay(date).toISOString();
      }
    }
    if (resolvedDateTo) {
      const date = new Date(resolvedDateTo);
      if (isValid(date)) {
        payload.resolvedDateTo = endOfDay(date).toISOString();
      }
    }
    if (escalationDateFrom) {
      const date = new Date(escalationDateFrom);
      if (isValid(date)) {
        payload.escalationDateFrom = startOfDay(date).toISOString();
      }
    }
    if (escalationDateTo) {
      const date = new Date(escalationDateTo);
      if (isValid(date)) {
        payload.escalationDateTo = endOfDay(date).toISOString();
      }
    }

    return { ...payload, ...overrides };
  };

  useEffect(() => {
    dispatch(getAllFlagsData(getFlagPayload()));
  }, [searchParams, sortInfo]);

  const handleSortClick = useCallback((newSortInfo: SortInfo) => {
    dispatch(setSortInfo(newSortInfo));
  }, []);

  const getMoreData = useCallback(
    (offset = 0) => {
      const payload = getFlagPayload({ offset });
      dispatch(getAllFlagsData(payload));
    },
    [getFlagPayload]
  );

  const loadMoreItems = useCallback(
    (startIndex: number, stopIndex: number) => {
      if (allFlagsData && stopIndex > allFlagsData.length) {
        getMoreData && getMoreData(startIndex);
      }
    },
    [allFlagsData, getMoreData]
  );

  const tableHeaders = [
    { title: FlagsTableHeader.Member, fieldName: FlagsSortField.User },
    { title: FlagsTableHeader.FlagDetails, fieldName: FlagsSortField.FlagDetails },
    { title: FlagsTableHeader.FlagAuthor, fieldName: FlagsSortField.Author },
    { title: FlagsTableHeader.Escalated, fieldName: FlagsSortField.CreatedAt },
    { title: FlagsTableHeader.Resolved, fieldName: FlagsSortField.ResolvedAt },
    { title: FlagsTableHeader.Status, fieldName: FlagsSortField.Status },
    { title: FlagsTableHeader.Actions },
  ];

  return isFlagsEnabled ? (
    isRejected ? (
      <DataErrorView error={allFlags.error} />
    ) : (
      <div className={styles.root}>
        <div className={styles.header}>
          <div className={styles.title}>
            Flags{' '}
            {!isDataLoading && (
              <Typography component="span" variant="body2" sx={{ marginLeft: '16px' }}>
                {totalCount} {totalCount === 1 ? 'flag' : 'flags'} found
              </Typography>
            )}
          </div>
          <AllFlagsFilters
            tomData={tomData ?? []}
            prData={prs.data ?? []}
            allDeparttmentsData={allDepartments.data ?? []}
            isLoading={isDataLoading}
            isFilterLoading={isFilterDataLoading}
            initialValues={initialValues}
          />
        </div>
        <FlagsTable
          isDataLoading={isDataLoading}
          limit={ALL_FLAGS_FETCH_LIMIT}
          loadMoreItems={loadMoreItems}
          isVirtualized
          data={{
            data: allFlagsData ?? [],
            state: allFlags.state,
          }}
          tableHeaders={tableHeaders}
          sortInfo={sortInfo}
          onSortClick={handleSortClick}
        />
      </div>
    )
  ) : (
    <AccessDeniedView />
  );
};
